<template>
  <div class="forgot-password">
    <h2 class="title">
      {{ $t('message.signIn.forgotPassword') }}
    </h2>
    <p v-show="status === Status.Pending" class="desc">
      {{ $t('message.signIn.forgotDesc') }}
    </p>
    <el-form
      v-show="status === Status.Pending"
      ref="resetForm"
      size="large"
      class="forgot-password-form"
      :model="form"
      label-position="top"
      @submit.prevent
    >
      <el-form-item
        :label="$t('message.signIn.email')"
        prop="email"
        :rules="emailRules"
        :error="formError"
      >
        <el-autocomplete
          ref="email"
          v-model="form.email"
          :fetch-suggestions="emailSuggestions"
          clearable
          class="email-input"
          :placeholder="$t('message.signIn.emailPlaceholder')"
          :disabled="loading"
          @select="onSelectSuggestion"
          @keyup.enter="doReset"
        />
      </el-form-item>
      <el-form-item class="btns">
        <el-button
          ref="actionBtn"
          type="primary"
          round
          class="btn"
          size="large"
          :loading="loading"
          @click="doReset"
        >
          {{ $t('message.common.submit') }}
        </el-button>
      </el-form-item>
    </el-form>
    <div v-if="status === Status.Success" class="result">
      <img src="@/assets/svg-icons/checkfill.svg" class="icon" alt="" />
      <div class="we-send">{{ $t('message.signIn.sendResetEmailSuccess.title') }}</div>
      <p class="tips">
        <span v-html="$t('message.signIn.sendResetEmailSuccess.content')" />
        <b class="reSendBtn" text :loading="loading" @click="doResend">
          {{ $t('message.signIn.sendResetEmailSuccess.resend') }} </b
        >.
      </p>
      <el-button type="primary" round size="large" class="reset-btn" @click="goBack">
        {{ $t('message.signIn.sendResetEmailSuccess.back') }}
      </el-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onActivated } from 'vue'
import { ElMessage } from 'element-plus'
import { useI18n } from 'vue-i18n'
import { emailSuggestions } from './helper'
import { sendValidation } from '@/api/user'

const emits = defineEmits<{
  (e: 'back'): void
}>()

enum Status {
  Pending = 1,
  Success = 2
}

const status = ref<Status>(Status.Pending)
const { t } = useI18n()
const form = ref({
  email: '',
  password: ''
})
const emailRules = ref([
  {
    required: true,
    message: t('message.signIn.emailPlaceholder'),
    trigger: ['change', 'blur']
  }
])
const formError = ref('')
const email = ref()
const actionBtn = ref()
const onSelectSuggestion = () => {
  // el-button 需要通过内部的 ref 来 focus
  actionBtn.value.ref.focus()
}
const resetForm = ref()
const loading = ref(false)
const doReset = () => {
  resetForm.value.validate().then(
    () => {
      loading.value = true
      sendValidation(
        {
          userName: form.value.email
        },
        {
          silent: true
        }
      ).then((res) => {
        loading.value = false
        if (!res.success) {
          formError.value = res.rtn_msg
        } else {
          status.value = Status.Success
        }
      }).finally(() => {
        loading.value = false
      })
    },
    () => {}
  )
}
const goBack = () => {
  emits('back')
  setTimeout(() => {
    status.value = Status.Pending
  }, 350)
}
/**
 * 重新发送
 */
const doResend = () => {
  loading.value = true
  sendValidation(
    {
      userName: form.value.email
    },
    {
      silent: true
    }
  ).then((res) => {
    loading.value = false
    if (res.error.value) {
      ElMessage.error({
        message: res.error.value.message
      })
    } else {
      ElMessage.success({
        message: t('message.signIn.resendSuccess')
      })
    }
  }).finally(() => {
    loading.value = false
  })
}
onActivated(() => {
  setTimeout(() => {
    resetForm.value && resetForm.value.clearValidate()
  }, 50)
})
const updateEmail = (val: string) => {
  form.value.email = val
}
defineExpose({
  updateEmail
})
</script>

<style lang="scss" scoped>
.forgot-password {
  background: white;
  padding: 8px 14px 30px;
  position: relative;

  .title {
    margin-top: 7px;
    font-size: 18px;
    text-align: center;
    font-weight: 600;
  }

  .desc {
    margin-top: 14px;
    margin-bottom: 114px;
    font-size: 14px;
    text-align: left;
    color: #333333;
  }

  :deep(.email-input) {
    width: 100%;
  }

  .forgot-password-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }

  .btns {
    margin-top: 34px;

    &:deep(.el-form-item__content) {
      justify-content: center;

      .btn {
        margin: 0;
        width: 100%;
        font-weight: 500;
        font-size: 18px;
      }
    }
  }

  .result {
    text-align: center;

    .icon {
      width: 42px;
      height: 42px;
    }

    .we-send {
      margin: 7px 0 0;
      color: $c-black1;
      font-size: 15px;
      font-weight: 500;
    }

    .tips {
      margin: 26px 0;
      padding-left: 16px;
      text-align: left;

      .reSendBtn {
        color: #FF9F00;
        margin-left: 4px;
        cursor: pointer;
      }
    }

    .reset-btn {
      width: 100%;
      margin-bottom: 28px;
      font-weight: 500;
      font-size: 18px;
    }
  }
}
</style>

<style lang="scss">
.forgot-password-form {
  .el-form-item__label {
    font-weight: 600;
  }
}
</style>
